import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Space,
  Form,
  Select,
  Button,
  Tag,
  Col,
  Row,
  Popconfirm,
  Statistic,
} from "antd";
import useTable from "../../../hooks/table";
import CustomFunctions from "../../../dependencies/custom/custom-functions";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import ValuesStore from "../../../store/values-store";
import useModal from "../../../hooks/modal";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { buildPreschoolStudentsPayload } from "./preschoolAssessmentPayload";

function gradeLetter(grade) {
  return grade?.letterGrade ?? grade?.letter_grade;
}

const NurseryAssessment = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const details = location.state;
  const valuesStore = ValuesStore();
  const { row, readOnly } = details || {};
  const [subjectAssignment, setSubjectAssignment] = useState(row || {});
  const [disabled, setDisabled] = useState(readOnly || parseInt(row?.locked));
  const [showConfirm, setShowConfirm] = useState(false);
  const [statusOpt, setStatusOpt] = useState();
  const modal = useModal();

  const fallbackGrades = useMemo(() => {
    const storedGrades = valuesStore.getValue("preschool_grade_remark");
    return Array.isArray(storedGrades) ? storedGrades : [];
  }, []);

  const [learningAreas, setLearningAreas] = useState([]);
  const [grades, setGrades] = useState(fallbackGrades);

  const gradeOptions = useMemo(() => {
    return Array.isArray(grades)
      ? grades?.map((v) => {
          const letter = gradeLetter(v);
          return {
            value: v?.id,
            label: letter ? `${v?.remark} (${letter})` : v?.remark,
          };
        })
      : [];
  }, [grades]);

  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  function refreshSubjectAssignment() {
    return CustomFunctions.getSubjectAssignment(
      setDisabled,
      navigate,
      readOnly,
      subjectAssignment,
      false,
      setSubjectAssignment,
    );
  }

  async function getCompetenceSetup() {
    if (!subjectAssignment?.id) return;

    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings?.urls?.academics?.preschool_competences}/${subjectAssignment?.id}`,
      undefined,
      navigate,
    );

    if (res?.ok) {
      if (Array.isArray(res?.data?.learningAreas)) {
        setLearningAreas(res?.data?.learningAreas);
      }
      if (Array.isArray(res?.data?.gradingRemarks)) {
        setGrades(res?.data?.gradingRemarks);
      }
      setSubjectAssignment((prev) => ({
        ...prev,
        className: res?.data?.className ?? prev?.className,
        term: res?.data?.term ?? res?.data?.termName ?? prev?.term,
        academicYear: res?.data?.academicYear ?? prev?.academicYear,
      }));
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  }

  const columns = useMemo(
    () => [
      {
        title: "Student ID",
        dataIndex: "id",
        key: "id",
        render: (v, record) => <label>{v}</label>,
      },
      {
        title: "Student",
        key: "name",
        onCell: () => ({
          style: {
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
        }),
        width: 150,
        render: (v, record) => (
          <label>{CustomFunctions.formatStudentName(record)}</label>
        ),
      },
      ...(Array.isArray(learningAreas)
        ? learningAreas?.map((area) => {
            const competences = (
              Array.isArray(area?.competences) ? area.competences : []
            ).filter((competence) => competence?.competenceID != null);

            return {
              title: area?.columnName,
              key: `area-${area?.columnID}`,
              children: competences?.map((competence) => ({
                title: competence?.statement,
                key: `competence-${competence?.competenceID}`,
                width: 180,
                onCell: () => ({
                  style: {
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                }),
                render: (_, record) => (
                  <Form.Item
                    name={[record.id, competence?.competenceID]}
                    noStyle
                  >
                    <Select
                      placeholder="Select"
                      className="w-100"
                      options={gradeOptions}
                    />
                  </Form.Item>
                ),
              })),
            };
          })
        : []),
    ],
    [learningAreas, gradeOptions],
  );

  function setMarks(data, groupKey = "studentID") {
    const grouped = Utilities.groupBy(data, groupKey);
    let values = {};
    for (let student in grouped) {
      const studentDetails = grouped[student];
      values[student] = {};
      for (let entry of studentDetails) {
        if (entry?.competenceID != null) {
          values[student][entry.competenceID] = entry?.gradeID;
        }
      }
    }
    form.resetFields();
    form.setFieldsValue(values);
  }

  async function getMarks() {
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings?.urls?.academics?.preschool_scoresheet}/${subjectAssignment?.id}`,
      undefined,
      navigate,
    );
    if (res?.ok) {
      setMarks(res?.data);
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  }

  useEffect(() => {
    refreshSubjectAssignment();
    getCompetenceSetup();
  }, []);

  useEffect(() => {
    table.setColumns(columns);
  }, [columns]);

  useEffect(() => {
    CustomFunctions.getStudents(table, subjectAssignment, navigate, getMarks);
  }, []);

  const onFinish = async (isSubmit, isAutoSave = true) => {
    if (disabled) {
      Utilities.showNotification(undefined, "Action is forbidden");
      // setShowConfirm(false);
      return;
    }
    const students = buildPreschoolStudentsPayload(
      form.getFieldsValue(),
      learningAreas,
    );
    if (!Object.keys(students)?.length) {
      Utilities.showNotification(undefined, "There is no data to save");
      return;
    }
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.academics?.preschool_scoresheet,
      { subjectTeacherID: subjectAssignment?.id, students },
      navigate,
    );
    if (res?.ok) {
      if (isSubmit) {
        const res1 = await Utilities.fetchWithAuth(
          "POST",
          Settings?.urls?.academics?.submit,
          { subjectTeacherID: subjectAssignment?.id },
          navigate,
        );
        if (res1?.ok) {
          Utilities.showNotification(
            undefined,
            "Scores have been submitted successfully",
            "text-success",
          );
        } else {
          Utilities.showNotification(undefined, res1?.msg);
        }
        setShowConfirm(false);
        refreshSubjectAssignment();
      } else {
        Utilities.showNotification(
          undefined,
          "Scores have been saved successfully",
          "text-success",
        );
      }
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  };

  async function changeStatus() {
    modal.setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.academics?.update_status,
      { subjectTeacherID: subjectAssignment?.id, status: statusOpt },
      navigate,
    );
    if (res?.ok) {
      modal.setOpen(false);
      setStatusOpt(undefined);
      await refreshSubjectAssignment();
      Utilities.showNotification(
        undefined,
        "Status has been updated successfully",
        "text-success",
      );
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
    modal.setLoading(false);
  }

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Space wrap>
            <Statistic title="Teacher" value={subjectAssignment?.teacherName} />
            <Tag color="blue">{subjectAssignment?.status}</Tag>
          </Space>
        </Card>
      </Col>

      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic
            title="Class"
            value={subjectAssignment?.className}
            suffix={subjectAssignment?.streamName}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} lg={4}>
        <Card variant="borderless" className="shadow">
          <Statistic
            title="Academic Year"
            value={subjectAssignment?.academicYear}
            formatter={(val) => val}
          />
        </Card>
      </Col>
      <Col xs={24} md={12} lg={4}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Term" value={subjectAssignment?.term} />
        </Card>
      </Col>

      <Col xs={24}>
        <Card
          title={
            <>
              {" "}
              <Button
                className="me-1"
                onClick={(e) => navigate(-1)}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>{" "}
              Assessment Entry
            </>
          }
          bordered={false}
          extra={
            <Space wrap className="my-3">
              {!readOnly && (
                <Button
                  onClick={(e) => onFinish(false)}
                  icon={<i className="fas fa-save" />}
                  className="btn-success border-0 me-2x"
                >
                  Save{" "}
                </Button>
              )}
              {!readOnly && (
                <Popconfirm
                  title="Submit Scores"
                  description="Confirm scores submission? This action will lock the assessment sheet."
                  onConfirm={(e) => onFinish(true)}
                  onCancel={(e) => setShowConfirm(false)}
                  placement="left"
                  okText="Yes"
                  cancelText="No"
                  open={showConfirm}
                >
                  <Button
                    icon={<i className="fas fa-paper-plane" />}
                    className="btn-success border-0"
                    onClick={(e) => setShowConfirm(true)}
                  >
                    Submit{" "}
                  </Button>
                </Popconfirm>
              )}
              {!!disabled && (
                <Button
                  onClick={(e) => modal.setOpen(true)}
                  icon={<i className="fas fa-sync" />}
                  className="btn-secondary border-0 me-2x"
                >
                  Change Status{" "}
                </Button>
              )}
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Form form={form} layout="vertical" disabled={disabled}>
                <div className="table-responsivex">
                  {table.tableWithHeaderFooter(
                    CustomFunctions.tblCaption,
                    CustomFunctions.tblCaption,
                    {
                      className: "nursery-assessment-table",
                      bordered: true,
                      scroll: { x: "max-content" },
                    },
                  )}
                </div>
              </Form>
            </Col>
          </Row>
        </Card>
      </Col>
      {modal.modalJSX(
        changeStatus,
        <>
          <label>Status</label>
          <Select
            className="w-100"
            onChange={(v) => setStatusOpt(v)}
            value={statusOpt}
            placeholder="Please select status"
            options={[
              { value: "Released", label: "Release" },
              { value: "Submitted", label: "Submit" },
            ]}
          />
        </>,
        undefined,
        { title: "Change status" },
      )}
    </Row>
  );
};

export default NurseryAssessment;
